import React, { useEffect, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Helmet from 'react-helmet'
import { useParams, withRouter } from 'react-router-dom'

import Slide from '@material-ui/core/Slide'
import Moment from 'moment'

import CaseNavigationBar from '../components/case/CaseNavigationBar'
import FAIcon from '../components/FAIcon'
import AppointmentService from '../services/AppointmentsService'
import CasesService from '../services/CasesService'
import ProviderService from '../services/ProviderService'

import './CompleteAppointment.scss'

const CompleteAppointment = withRouter(({ history, location }) => {
  const { caseNumber, appointmentNumber } = useParams()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [caseModel, setCaseModel] = useState({})
  const [appointment, setAppointment] = useState({})
  const [providers, setProviders] = useState([])

  useEffect(() => {
    let isMounted = true

    if (!!caseNumber && !!appointmentNumber) {
      setIsLoading(true)

      const casePromise = CasesService.get(caseNumber).then(caseObj => {
        if (isMounted) setCaseModel(caseObj)
        return caseObj
      })

      const appointmentPromise = AppointmentService.get(caseNumber, appointmentNumber).then(appt => {
        if (isMounted) setAppointment(appt)
        return appt
      })

      const providersPromise = ProviderService.getProvidersForCase(caseNumber).then(provs => {
        if (isMounted) setProviders(provs || [])
        return provs
      })

      Promise.all([casePromise, appointmentPromise, providersPromise])
        .then(() => {
          if (isMounted) setIsLoading(false)
        })
        .catch(err => {
          if (isMounted) {
            setError(err.message)
            setIsLoading(false)
          }
        })
    }

    return () => {
      isMounted = false
    }
  }, [caseNumber, appointmentNumber])

  const handleClickExit = () => {
    const fromPath = location.state?.from
    if (fromPath) {
      history.push(fromPath)
    } else if (caseNumber) {
      history.push(`/cases/${caseNumber}`)
    } else {
      history.push('/')
    }
  }

  const getFormattedDateTimeValue = (startUtc, endUtc) => {
    if (startUtc) {
      if (appointment?.isAllDay) {
        const dateStr = Moment.utc(startUtc).format('dddd, MMMM DD, YYYY')
        return <p className="text-details">{dateStr}</p>
      }

      const mLocal = Moment.utc(startUtc).local()
      const dateStr = mLocal.format('dddd, MMMM DD, YYYY')
      const startTime = mLocal.format('h:mma')
      const endTime = endUtc ? Moment.utc(endUtc).local().format('h:mma') : ''

      return (
        <p className="text-details">
          {dateStr} &#8226; {startTime}
          {endTime ? `-${endTime}` : ''}
        </p>
      )
    }
    return ''
  }

  // Find provider match
  const appointmentProvider = appointment.provider || appointment.individualProvider
  const matchedProvider = providers.find(p => p.name === appointmentProvider)

  return (
    <React.Fragment>
      <Helmet
        title={`Complete Appointment Summary - Case ${caseNumber}`}
        bodyAttributes={{ style: 'background-color : #fff !important' }}
      />
      <Slide className="complete-appointment-container background" direction="up" in={true} mountOnEnter unmountOnExit>
        <div>
          <CaseNavigationBar
            title={
              caseModel.client
                ? `${caseModel.client.firstName?.toUpperCase()} ${caseModel.client.lastName?.toUpperCase()}`
                : ''
            }
            subtitle={`CASE NO: ${caseNumber}`}
            rightButtonText=""
            handleClickLeft={handleClickExit}
            caseNumber={caseNumber}
          />

          {!!error && (
            <Alert variant="danger" className="mb-0 pr-5 pl-5">
              <strong>Error:</strong> <pre className="mb-0">{error}</pre>
            </Alert>
          )}

          {!isLoading ? (
            <div className="container mt-5">
              <h4 className="mb-2">{appointment.title || 'Appointment Details'}</h4>
              {getFormattedDateTimeValue(appointment.startUtc, appointment.endUtc)}

              <hr className="my-4" />

              <div className="card shadow-sm border-0 bg-light p-4 rounded mb-4">
                <h5 className="text-primary mb-3">
                  <FAIcon name="hospital" className="mr-2" />
                  Provider Details
                </h5>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <span className="d-block text-muted small uppercase">Provider Name</span>
                    <strong className="lead">{appointment.provider || appointment.individualProvider || 'N/A'}</strong>
                  </div>
                  {matchedProvider && (
                    <React.Fragment>
                      <div className="col-md-6 mb-3">
                        <span className="d-block text-muted small uppercase">Phone</span>
                        <strong>{matchedProvider.phone || 'N/A'}</strong>
                      </div>
                      <div className="col-md-6 mb-3">
                        <span className="d-block text-muted small uppercase">Email</span>
                        <strong>{matchedProvider.email || 'N/A'}</strong>
                      </div>
                    </React.Fragment>
                  )}
                </div>

                <hr className="my-3" />

                <h5 className="text-primary mb-3">
                  <FAIcon name="map-marker-alt" className="mr-2" />
                  Address / Location
                </h5>
                <p className="lead mb-0">
                  {appointment.address?.line1 ? (
                    <React.Fragment>
                      {appointment.address.line1}
                      <br />
                      {appointment.address.city}, {appointment.address.state} {appointment.address.zip}
                    </React.Fragment>
                  ) : (
                    <span className="text-muted">No address provided</span>
                  )}
                </p>
              </div>

              <div className="d-flex justify-content-end">
                <button type="button" className="btn btn-secondary px-4" onClick={handleClickExit}>
                  Back
                </button>
              </div>
            </div>
          ) : (
            <div className="d-flex justify-content-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          )}
        </div>
      </Slide>
    </React.Fragment>
  )
})

export default CompleteAppointment
